import React, { useEffect, useState } from "react";
import { QUIZZES } from "../data/quizzes";
import CertificatePDF from "./CertificatePDF";
import { saveAs } from 'file-saver';

/**
 * Quiz component
 * Props:
 *  - quiz: the quiz object (required)
 *
 * Behavior:
 *  - Fully matches original app logic
 *  - Persists per-quiz state to localStorage under key "ems_quiz_state_v1"
 */

const STORAGE_KEY = "ems_quiz_state_v1";
const OPTION_ORDER = ["A", "B", "C", "D"];

function loadAllState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveAllState(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // ignore write errors
  }
}

export default function Quiz({ quiz }) {
  // primary UI state
  const [quizId, setQuizId] = useState(quiz.id);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  // aggregate scores (persisted per-quiz too)
  const [answered, setAnswered] = useState(0);
  const [correct, setCorrect] = useState(0);

  // convenience
  const totalQuestions = quiz.questions.length;
  const lastIndex = totalQuestions - 1;
  const question = quiz.questions[index];

  const isMobile = window.innerWidth < 800

  // Load per-quiz saved state whenever quiz prop or quizId changes
  useEffect(() => {
    // If the incoming prop's id differs, update quizId
    if (quiz.id !== quizId) {
      setQuizId(quiz.id);
    }

    // Load from storage
    const all = loadAllState();
    const saved = all[quiz.id];

    if (saved) {
      // clamp index to available questions
      const idx = Math.min(saved.index ?? 0, Math.max(0, totalQuestions - 1));
      setIndex(idx);
      setAnswered(saved.answeredCount ?? 0);
      setCorrect(saved.correctCount ?? 0);

      const per = saved.perQuestion || {};
      const entry = per[idx];

      if (entry && entry.submitted) {
        setSubmitted(true);
        setSelected(entry.selected ?? null);
        setShowExplanation(!!entry.showExplanation);
      } else {
        setSubmitted(false);
        setSelected(entry?.selected ?? null);
        setShowExplanation(false);
      }
    } else {
      // no saved state: initialize clean
      setIndex(0);
      setSelected(null);
      setSubmitted(false);
      setShowExplanation(false);
      setAnswered(0);
      setCorrect(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz.id]);

  // Helper: persist current quiz state to localStorage
  function persistQuizState(newState) {
    const all = loadAllState();
    all[quizId] = {
      index: newState.index ?? index,
      answeredCount: newState.answeredCount ?? answered,
      correctCount: newState.correctCount ?? correct,
      perQuestion: newState.perQuestion ?? (all[quizId]?.perQuestion ?? {}),
    };
    saveAllState(all);
  }

  // When user selects an option (only allowed before submit)
  const handleSelect = (key) => {
    if (submitted) return;
    setSelected(key);

    // Optionally persist tentative choice (not counted until submit)
    const all = loadAllState();
    const saved = all[quizId] || { index: 0, answeredCount: answered, correctCount: correct, perQuestion: {} };
    const per = saved.perQuestion || {};
    per[index] = {
      ...(per[index] || {}),
      selected: key,
      submitted: per[index]?.submitted || false,
      isCorrect: per[index]?.isCorrect || false,
      showExplanation: per[index]?.showExplanation || false,
    };
    saved.perQuestion = per;
    all[quizId] = saved;
    saveAllState(all);
  };

  // Submit handler
  const submit = () => {
    if (!selected || submitted) return;

    const isCorrect = selected === question.answer;

    // Update counts only if this question wasn't already marked submitted
    const all = loadAllState();
    const saved = all[quizId] || { index: 0, answeredCount: answered, correctCount: correct, perQuestion: {} };
    const per = saved.perQuestion || {};

    const already = per[index]?.submitted;
    let newAnswered = saved.answeredCount ?? answered;
    let newCorrect = saved.correctCount ?? correct;

    if (!already) {
      newAnswered = (newAnswered || 0) + 1;
      if (isCorrect) newCorrect = (newCorrect || 0) + 1;
    }

    // Mark this question submitted
    per[index] = {
      selected,
      submitted: true,
      isCorrect,
      showExplanation: per[index]?.showExplanation || false,
    };

    saved.perQuestion = per;
    saved.index = index;
    saved.answeredCount = newAnswered;
    saved.correctCount = newCorrect;
    all[quizId] = saved;
    saveAllState(all);

    // Apply to UI
    setAnswered(newAnswered);
    setCorrect(newCorrect);
    setSubmitted(true);
  };

  // Next handler
  const next = () => {
    if (!submitted) return; // original behavior: next only after submit
    if (index >= lastIndex) return;

    const newIndex = index + 1;

    // Load saved entry for newIndex
    const all = loadAllState();
    const saved = all[quizId] || {};
    const per = saved.perQuestion || {};
    const entry = per[newIndex];

    setIndex(newIndex);

    if (entry) {
      setSelected(entry.selected ?? null);
      setSubmitted(!!entry.submitted);
      setShowExplanation(!!entry.showExplanation);
    } else {
      setSelected(null);
      setSubmitted(false);
      setShowExplanation(false);
    }

    // Persist index change
    saved.index = newIndex;
    all[quizId] = {
      ...saved,
      answeredCount: saved.answeredCount ?? answered,
      correctCount: saved.correctCount ?? correct,
      perQuestion: per,
    };
    saveAllState(all);
  };

  // Toggle explanation (only if submitted)
  const toggleExplanation = () => {
    if (!submitted) return;
    const newShow = !showExplanation;
    setShowExplanation(newShow);

    // persist this flag
    const all = loadAllState();
    const saved = all[quizId] || {};
    const per = saved.perQuestion || {};
    per[index] = {
      ...(per[index] || {}),
      selected: per[index]?.selected ?? selected,
      submitted: !!per[index]?.submitted || submitted,
      isCorrect: per[index]?.isCorrect ?? (selected === question.answer),
      showExplanation: newShow,
    };
    saved.perQuestion = per;
    saved.index = index;
    saved.answeredCount = saved.answeredCount ?? answered;
    saved.correctCount = saved.correctCount ?? correct;
    all[quizId] = saved;
    saveAllState(all);
  };

  // Restart current quiz (optional helper)
  // Not exposed as a button to match original — you can add if needed.
  const resetQuiz = () => {
    if (window.confirm('Are you sure you want to restart the quiz?')) {
      const all = loadAllState();
      delete all[quizId];
      saveAllState(all);
      // Reset UI
      setIndex(0);
      setSelected(null);
      setSubmitted(false);
      setShowExplanation(false);
      setAnswered(0);
      setCorrect(0);
    }
  };

  // Render helpers
  const progressPercent = totalQuestions === 0 ? 0 : Math.round((answered / totalQuestions) * 100);
  const scoreText = answered === 0 ? "Score: -" : `Score: ${correct}/${answered} (${Math.round((correct / answered) * 100)}%)`;

  const certificateRef = React.useRef(null);
  const downloadLinkRef = React.useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [participantName, setParticipantName] = useState("");

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.firstName) {
        setParticipantName(`${user.firstName} ${user.lastName || ''}`.trim());
      } else {
        setParticipantName("Anonymous Participant");
      }
    } catch {
      setParticipantName("Anonymous Participant");
    }
  }, []);

  const generatePDF = () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);

    try {
      const name = participantName || 'Anonymous Participant';
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
      const certId = `EMS-${Math.floor(100000 + Math.random() * 900000)}`;

      // Build the full HTML for the certificate in a self-contained string
      const certHtml = `<!DOCTYPE html>
<html>
<head>
  <title>Course Certificate - ${name}</title>
  <meta charset="UTF-8">
  <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Great+Vibes&family=Montserrat:wght@400;500;600&display=swap" rel="stylesheet">
  <style>
    @page {
      size: letter landscape;
      margin: 0;
    }
    * { 
      box-sizing: border-box; 
      margin: 0; 
      padding: 0; 
      -webkit-print-color-adjust: exact !important; 
      print-color-adjust: exact !important; 
    }
    body {
      width: 11in;
      height: 8.5in;
      background: #fdfbf7;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cert-container {
      width: 10.5in;
      height: 8in;
      background: white;
      padding: 15px;
      position: relative;
      box-shadow: 0 0 15px rgba(0,0,0,0.1);
    }
    .cert-outer-border {
      width: 100%;
      height: 100%;
      border: 3px solid #0f2c59; /* Deep Navy */
      padding: 6px;
    }
    .cert-inner-border {
      width: 100%;
      height: 100%;
      border: 2px solid #02af90; /* Theme Green */
      padding: 40px 60px;
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      background: linear-gradient(to bottom, #ffffff 0%, #faf8f5 100%);
    }
    
    /* Corner Ornaments using CSS */
    .corner { position: absolute; width: 30px; height: 30px; border: 2px solid #02af90; }
    .top-left { top: 10px; left: 10px; border-right: none; border-bottom: none; }
    .top-right { top: 10px; right: 10px; border-left: none; border-bottom: none; }
    .bottom-left { bottom: 10px; left: 10px; border-right: none; border-top: none; }
    .bottom-right { bottom: 10px; right: 10px; border-left: none; border-top: none; }

    .header-text {
      font-family: 'Cinzel', serif;
      font-size: 16px;
      color: #02af90;
      letter-spacing: 4px;
      margin-bottom: 15px;
      text-transform: uppercase;
    }
    .cert-title { 
      font-family: 'Cinzel', serif;
      font-size: 42px; 
      font-weight: 700; 
      color: #0f2c59;
      letter-spacing: 2px;
      text-transform: uppercase;
      margin-bottom: 30px;
      text-align: center;
    }
    .cert-subtitle { 
      font-family: 'Montserrat', sans-serif;
      font-size: 14px; 
      color: #555;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 20px;
    }
    .cert-name { 
      font-family: 'Great Vibes', cursive;
      font-size: 56px; 
      color: #0f2c59;
      margin-bottom: 25px;
      line-height: 1.2;
      border-bottom: 1px solid #02af90;
      padding: 0 40px 10px 40px;
      min-width: 50%;
      text-align: center;
    }
    .cert-text { 
      font-family: 'Montserrat', sans-serif;
      font-size: 14px; 
      line-height: 1.6; 
      color: #333;
      text-align: center;
      max-width: 70%;
      margin-bottom: 15px;
    }
    .cert-course { 
      font-family: 'Cinzel', serif;
      font-size: 22px; 
      font-weight: 600; 
      color: #0f2c59;
      margin: 15px 0 25px 0;
    }
    
    .footer-section {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-top: auto;
      padding: 0 20px;
    }
    .details-box {
      font-family: 'Montserrat', sans-serif;
      font-size: 11px;
      color: #555;
      text-align: left;
    }
    .details-box div { margin-bottom: 5px; }
    .details-box span { font-weight: 600; color: #0f2c59; }
    
    .signature-box {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .cert-signature-wrap {
      width: 220px;
      height: 66px;
      margin-bottom: -3px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .cert-signature {
      width: 220px;
      height: 66px;
      object-fit: contain;
      display: block;
    }
    .signature-line {
      width: 250px;
      border-bottom: 1px solid #0f2c59;
      margin-bottom: 8px;
    }
    .cert-sig-name { 
      font-family: 'Montserrat', sans-serif;
      font-size: 13px; 
      font-weight: 600; 
      color: #0f2c59;
    }
    .cert-sig-title { 
      font-family: 'Montserrat', sans-serif;
      font-size: 11px; 
      color: #666; 
    }
    
    .seal {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: #0f2c59;
      border: 2px dashed #02af90;
      position: absolute;
      bottom: 40px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #02af90;
      font-family: 'Cinzel', serif;
      font-size: 10px;
      text-align: center;
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }
    .seal-inner {
      width: 70px;
      height: 70px;
      border-radius: 50%;
      border: 1px solid #02af90;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }

    .cert-disclaimer { 
      position: absolute;
      bottom: 15px;
      width: 100%;
      text-align: center;
      font-family: 'Montserrat', sans-serif;
      font-size: 9px; 
      color: #999; 
    }
  </style>
</head>
<body>
  <div class="cert-container">
    <div class="cert-outer-border">
      <div class="cert-inner-border">
        <div class="corner top-left"></div>
        <div class="corner top-right"></div>
        <div class="corner bottom-left"></div>
        <div class="corner bottom-right"></div>

        <div class="header-text">Caribbean EMS Initiative</div>
        <div class="cert-title">Certificate of Completion</div>
        
        <div class="cert-subtitle">This certifies that</div>
        
        <div class="cert-name">${name}</div>
        
        <div class="cert-text">has successfully completed the comprehensive requirements of the</div>
        <div class="cert-course">Dr. Dangleben EMS Online Course</div>
        
        <div class="cert-text">
          including all educational modules, practical scenario assessments, <br>
          and the final comprehensive examination with a score of 80% or higher.
        </div>
        
        <div class="seal">
          <div class="seal-inner">
            <div>OFFICIAL</div>
            <div>COURSE</div>
          </div>
        </div>

        <div class="footer-section">
          <div class="details-box">
            <div><span>Certificate ID:</span> ${certId}</div>
            <div><span>Date Issued:</span> ${date}</div>
          </div>
          
          <div class="signature-box">
            <div class="cert-signature-wrap"><svg class="cert-signature" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 700 209" aria-label="Dale A. Dangleben signature"><g fill="#0f2c59"><path d="M 177 135 L 174 134 L 162 138 L 158 138 L 155 135 L 147 135 L 138 139 L 134 143 L 134 147 L 136 149 L 140 150 L 147 148 L 152 145 L 160 145 L 166 143 L 167 147 L 171 148 L 177 144 L 177 142 L 173 140 L 177 137 Z"/><path d="M 180 138 L 180 140 L 184 139 L 185 143 L 190 148 L 199 149 L 212 143 L 221 143 L 227 145 L 241 144 L 257 139 L 259 136 L 255 135 L 251 137 L 246 137 L 239 139 L 228 139 L 227 138 L 230 134 L 229 128 L 226 126 L 220 126 L 214 130 L 209 138 L 203 141 L 193 142 L 191 140 L 191 134 L 196 123 L 202 115 L 220 97 L 221 94 L 220 91 L 215 91 L 203 103 L 192 117 L 187 125 L 185 131 Z"/><path d="M 464 114 L 465 119 L 470 127 L 476 130 L 484 130 L 496 125 L 501 121 L 506 125 L 513 125 L 518 122 L 521 124 L 524 124 L 528 122 L 536 113 L 540 117 L 545 119 L 560 118 L 654 98 L 672 96 L 673 95 L 680 95 L 681 94 L 697 93 L 698 89 L 690 88 L 689 89 L 678 89 L 677 90 L 662 91 L 557 112 L 545 112 L 542 109 L 542 102 L 553 79 L 553 74 L 549 72 L 541 80 L 535 97 L 535 105 L 527 115 L 525 113 L 521 113 L 512 118 L 511 117 L 511 111 L 506 109 L 494 118 L 486 122 L 477 123 L 473 119 L 472 116 L 472 104 L 478 89 L 484 82 L 484 80 L 482 78 L 479 78 L 473 83 L 468 92 L 465 101 Z"/><path d="M 135 74 L 128 67 L 119 62 L 102 58 L 88 58 L 87 59 L 75 60 L 67 62 L 54 68 L 52 71 L 52 74 L 56 82 L 58 82 L 60 80 L 59 78 L 59 73 L 60 72 L 79 66 L 102 65 L 112 67 L 121 71 L 127 76 L 132 85 L 132 101 L 128 114 L 119 130 L 106 146 L 93 158 L 80 167 L 68 173 L 46 179 L 35 179 L 34 180 L 21 179 L 13 177 L 5 171 L 1 173 L 1 178 L 8 183 L 19 186 L 31 186 L 32 187 L 34 186 L 46 186 L 60 183 L 76 177 L 98 163 L 120 141 L 132 123 L 137 112 L 140 100 L 140 86 L 138 79 Z"/><path d="M 452 52 L 449 52 L 442 58 L 414 89 L 392 121 L 376 151 L 369 170 L 367 186 L 371 198 L 377 204 L 384 207 L 401 207 L 422 200 L 431 195 L 440 188 L 447 180 L 452 171 L 454 164 L 454 152 L 451 143 L 446 136 L 437 129 L 429 126 L 418 126 L 416 127 L 413 124 L 407 124 L 399 130 L 398 132 L 399 137 L 408 138 L 417 133 L 424 132 L 434 135 L 442 142 L 447 152 L 447 165 L 442 176 L 431 187 L 425 191 L 413 197 L 399 201 L 386 201 L 383 200 L 377 195 L 374 188 L 374 177 L 376 168 L 383 150 L 395 127 L 418 93 L 433 75 L 452 55 Z"/><path d="M 374 13 L 362 6 L 345 1 L 321 1 L 320 2 L 310 3 L 291 9 L 273 18 L 257 30 L 249 39 L 245 47 L 245 56 L 249 62 L 251 62 L 252 60 L 250 56 L 250 51 L 252 46 L 265 32 L 273 26 L 294 15 L 309 10 L 318 8 L 325 8 L 326 7 L 340 7 L 354 10 L 363 14 L 369 18 L 377 26 L 381 32 L 384 40 L 385 61 L 381 77 L 373 93 L 358 113 L 340 130 L 320 144 L 294 157 L 256 169 L 254 167 L 247 168 L 243 170 L 242 174 L 246 177 L 256 176 L 279 170 L 298 163 L 317 154 L 335 143 L 348 133 L 368 113 L 384 89 L 389 77 L 392 64 L 392 43 L 389 32 L 383 22 Z"/></g></svg></div>
            <div class="signature-line"></div>
            <div class="cert-sig-name">Dale A. Dangleben, MD, FACS</div>
            <div class="cert-sig-title">Founder & Medical Director</div>
          </div>
        </div>
        <div class="cert-actions no-print">
          <button onclick="window.print()">Print Certificate</button>
          <button onclick="window.print()">Save as PDF</button>
        </div>
        
        <div class="cert-disclaimer">
          This certificate represents successful completion of an educational program and does not constitute a state or national EMT license.
        </div>
      </div>
    </div>
  </div>
  <script>
    window.onload = function() {
      setTimeout(function() {
        printNow();
      }, 800);
    };
  </script>
</body>
</html>`;

      // Open a small popup and write the certificate HTML into it
      const printWindow = window.open('', '_blank', 'width=820,height=620,scrollbars=no,menubar=no,toolbar=no,location=no,status=no');
      if (!printWindow) {
        alert("Please allow popups for this site to download your certificate.");
        return;
      }
      printWindow.document.write(certHtml);
      printWindow.document.close();

    } catch (error) {
      console.error("Error generating certificate:", error);
      alert("There was an error generating your certificate. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };


  return (
    <div className="quiz-root1">
      <div className="wrapper1 container" style={{ paddingLeft: isMobile ? 15 : 30, paddingRight: isMobile ? 15 : 30 }}>
        <header className="header1" style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'transparent' }}>
          <div className="brand1" style={{ marginTop: 8 }}>
            <div className="brand-title1">Dr. Dangleben's Caribbean EMS Quiz</div>
            <div className="brand-subtitle1">Practice mixed EMS questions and Caribbean-specific scenarios.</div>
          </div>
          <div className="quiz-select1" role="tablist" aria-label="Quizzes">
            {/* Keep single button for current quiz (to match your provided structure); if you pass 'quizzes' prop we could map them */}
            <button
              key={quiz.id}
              className={quizId === quiz.id ? "active" : ""}
              onClick={() => {
                // If user clicks this while already on this quiz, do nothing.
                if (quizId === quiz.id) return;
                // Switch quiz and load its saved state will be handled by the effect above (it listens to quiz.id)
                setQuizId(quiz.id);
              }}
            >
              {quiz.title}
            </button>
          </div>
        </header>

        <main className="main1">

          <div className="score-bar1">
            <span className="score-label1">Score</span>
            <span className="score-values1">
              <span className="score-correct1">{correct}</span>
              <span className="score-separator1">/</span>
              <span className="score-answered1">{answered}</span>
            </span>
            {answered > 0 && (
              <span className="score-percent1">
                {Math.round((correct / answered) * 100)}%
              </span>
            )}
          </div>

          {quizId === "npCBScjV0yL8T1gq0Qgv" && (
            <div style={{
              margin: '20px 0',
              padding: '20px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '15px'
            }}>
              <div style={{ color: '#fff' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎓 Official Course Certification
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.9, marginTop: '4px' }}>
                  Score 80% or higher overall to unlock your certificate.
                </div>
              </div>
              <button
                onClick={generatePDF}
                disabled={(correct / totalQuestions) < 0.8 || isGeneratingPdf}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: ((correct / totalQuestions) >= 0.8 && !isGeneratingPdf) ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                  color: ((correct / totalQuestions) >= 0.8 && !isGeneratingPdf) ? '#059669' : '#1f2937',
                  fontWeight: 'bold',
                  cursor: ((correct / totalQuestions) >= 0.8 && !isGeneratingPdf) ? 'pointer' : 'not-allowed',
                  boxShadow: ((correct / totalQuestions) >= 0.8 && !isGeneratingPdf) ? '0 4px 10px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                {isGeneratingPdf ? 'Generating...' : 'Get Certificate'}
              </button>
              {/* <button
                onClick={() => {
                  setAnswered(totalQuestions);
                  setCorrect(totalQuestions);
                  setSubmitted(true);
                }}
                style={{ marginLeft: '10px', padding: '10px', background: '#eab308', color: '#fff', border: 'none', borderRadius: '5px' }}>
                Test: Pass Quiz
              </button> */}

            </div>
          )}

          <div className="progress-bar1" aria-hidden>
            <div className="progress-fill1" style={{ width: `${progressPercent}%` }} />
          </div>

          <section className="quiz-card1" aria-live="polite">
            <div className="quiz-q-position1">Question {index + 1} of {totalQuestions}</div>

            <div className="question-text1">{question.number}. {question.question}</div>

            <ul className="options1" role="list">
              {OPTION_ORDER.map((key) => {
                const value = question.options[key];
                if (!value) return null;

                // compute classes carefully
                let classes = "option1";

                if (!submitted && selected === key) {
                  classes += " selected";
                }

                if (submitted) {
                  if (key === question.answer) {
                    classes += " correct";
                  } else if (key === selected) {
                    classes += " incorrect";
                  }
                }

                return (
                  <li
                    key={key}
                    className={classes}
                    role="listitem"
                    onClick={() => handleSelect(key)}
                    // prevent pointer events after submit
                    style={{ pointerEvents: submitted ? "none" : "auto" }}
                    aria-pressed={selected === key}
                  >
                    <strong>{key}.</strong> {value}
                  </li>
                );
              })}
            </ul>

            {/* Feedback is shown only after submit (matches original) */}
            {submitted && (
              <div className={`feedback1 ${selected === question.answer ? "correct" : "incorrect"}`}>
                {selected === question.answer ? "Correct!" : `Correct answer: ${question.answer}`}
              </div>
            )}

            {/* Explanation toggled after submit and persisted */}
            {showExplanation && (
              <div className="explanation1">{question.explanation}</div>
            )}

            <div className="actions1" style={{ marginTop: 20 }}>


              <button
                className="secondary1"
                onClick={resetQuiz}
                style={{ display: "inline-block" }}
              >
                Reset Quiz
              </button>

              <button
                className="secondary1"
                disabled={!submitted}
                onClick={toggleExplanation}
                aria-disabled={!submitted}
              >
                Show / Hide Explanation
              </button>

              <button
                className="secondary1"
                disabled={!submitted || index >= lastIndex}
                onClick={next}
                aria-disabled={!submitted || index >= lastIndex}
              >
                Next Question
              </button>

              <button
                className="primary1"
                onClick={submit}
                disabled={!selected || submitted}
                aria-disabled={!selected || submitted}
              >
                Submit Answer
              </button>

            </div>

          </section>
        </main>
      </div>

      <CertificatePDF
        ref={certificateRef}
        participantName={participantName}
        certificateId={`EMS-${Math.floor(100000 + Math.random() * 900000)}`}
        dateIssued={new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' })}
      />

      {/* Hidden link used strictly for triggering robust browser downloads */}
      <a ref={downloadLinkRef} style={{ display: 'none' }} aria-hidden="true" href="#download" />
    </div>
  );
}






// import { useEffect, useState } from "react";
// import { QUIZZES } from "../data/quizzes";

// export default function Quiz({ quiz, quizzes }) {

//   const [index, setIndex] = useState(0);
//   const [selected, setSelected] = useState(null);
//   const [answered, setAnswered] = useState(5);
//   const [correct, setCorrect] = useState(0);
//   const [showExplanation, setShowExplanation] = useState(false);

//   const [quizId, setQuizId] = useState(quiz.id);
//   const question = quiz.questions[index];

//   const [submitted, setSubmitted] = useState(false);

//   useEffect(() => {
//     setIndex(0);
//     setSelected(null);
//     setShowExplanation(false);
//   }, [quizId]);

//   const submit = () => {
//     if (!selected) return;
//     setAnswered(a => a + 1);
//     if (selected === question.answer) {
//       setCorrect(c => c + 1);
//     }
//     setSubmitted(true);
//   };

//   const next = () => {
//     setSelected(null);
//     setShowExplanation(false);
//     setSubmitted(false);
//     setIndex(i => i + 1);
//   };

//   return (
//     <div className="quiz-root1" style={{ backgroundColor: '#069379ff' }}>
//       <div className="wrapper1 container" style={{}}>
//         <header className="header1" style={{ display: 'flex', flexDirection: 'column' }}>
//           <div className="brand1" style={{ marginTop: 20 }}>
//             <div className="brand-title1">
//               Dr. Dangleben's Caribbean EMS Quiz
//             </div>
//             <div className="brand-subtitle1">
//               Practice mixed EMS questions and Caribbean-specific scenarios.
//             </div>
//           </div>

//           <div className="quiz-select1">
//             <button
//               key={quiz.id}
//               className={quizId === quiz.id ? "active" : ""}
//               onClick={() => setQuizId(quiz.id)}
//             >
//               {quiz.title}
//             </button>
//           </div>
//         </header>

//         <main className="main1">
//           <div className="score-pill1">
//             {answered === 0
//               ? "Score: -"
//               : `Score: ${correct}/${answered} (${Math.round(
//                 (correct / answered) * 100
//               )}%)`}
//           </div>
//           <div className="progress-bar1">
//             <div
//               className="progress-fill1"
//               style={{
//                 width: `${Math.round((answered / quiz.questions.length) * 100)}%`
//               }}
//             />
//           </div>

//           <section className="quiz-card1">
//             <div className="quiz-q-position1">
//               Question {index + 1} of {quiz.questions.length}
//             </div>

//             <div className="question-text1">
//               {question.number}. {question.question}
//             </div>

//             <ul className="options1">
//               {["A", "B", "C", "D"].map((key) => {
//                 const value = question.options[key];
//                 if (!value) return null;
//                 let classes = "option1";
//                 if (submitted) {
//                   if (key === question.answer) {
//                     classes += " correct";
//                   } else if (key === selected) {
//                     classes += " incorrect";
//                   }
//                 } else if (selected === key) {
//                   classes += " selected";
//                 }
//                 return (
//                   <li
//                     key={key}
//                     className={classes}
//                     onClick={() => {
//                       if (!submitted) setSelected(key);
//                     }}
//                   >
//                     <strong>{key}.</strong> {value}
//                   </li>
//                 );
//               })}
//             </ul>


//             {submitted && (
//               <div
//                 className={`feedback1 ${selected === question.answer ? "correct" : "incorrect"
//                   }`}
//               >
//                 {selected === question.answer
//                   ? "Correct!"
//                   : `Correct answer: ${question.answer}`}
//               </div>
//             )}


//             {showExplanation && (
//               <div className="explanation1">
//                 {question.explanation}
//               </div>
//             )}

//             <div className="actions1">
//               <button
//                 className="secondary1"
//                 disabled={!selected}
//                 onClick={() => setShowExplanation(!showExplanation)}
//               >
//                 Show / Hide Explanation
//               </button>

//               <button
//                 className="secondary1"
//                 disabled={!selected || index >= quiz.questions.length - 1}
//                 onClick={next}
//               >
//                 Next Question
//               </button>

//               <button
//                 className="primary1"
//                 onClick={submit}
//                 disabled={!selected}
//               >
//                 Submit Answer
//               </button>
//             </div>
//           </section>
//         </main>
//       </div>
//     </div>
//   );
// }
